import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Shirt } from '@sorare/core/src/atoms/icons/shirts/Shirt.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { sportLogos } from '@sorare/core/src/components/SportLogo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { LeaguePageHeader_competitionCardCollections } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: 0 var(--double-unit);
`;
const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

const SportLogoWrapper = styled.div`
  & * {
    height: 64px;
  }
`;

const CompetitionLogo = styled.img`
  height: 64px;
  object-fit: contain;
`;

const StyledShirt = styled(Shirt)`
  color: var(--c-nd-600);
`;

type Props = {
  competitionCardCollections: Nullable<LeaguePageHeader_competitionCardCollections>;
  loading: boolean;
};

export const LeaguePageHeader = ({
  competitionCardCollections,
  loading,
}: Props) => {
  const { sport } = useSportContext();
  const { competition, myStats } = competitionCardCollections || {};

  const { ownedPlayersCount, totalPlayersCount } = myStats ?? {
    ownedPlayersCount: 0,
    totalPlayersCount: 1,
  };

  return (
    <Root center gap={3}>
      {loading ? (
        <div style={{ height: 64 }}>
          <LoadingIndicator small />
        </div>
      ) : (
        <>
          {competition?.pictureUrl ? (
            <CompetitionLogo
              src={competition?.pictureUrl}
              alt={competition?.displayName}
            />
          ) : (
            <SportLogoWrapper>{sportLogos[sport]}</SportLogoWrapper>
          )}
        </>
      )}
      <SBHorizontal gap={4}>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage {...glossary.players} />
          </LabelS>
          <Horizontal gap={0.5}>
            <LabelL bold>{ownedPlayersCount}</LabelL>/
            <LabelL bold color="var(--c-nd-500)">
              {totalPlayersCount}
            </LabelL>
            <StyledShirt />
          </Horizontal>
        </Vertical>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage defaultMessage="Players collected" />
          </LabelS>
          <Horizontal>
            <StyledCircularProgress
              progress={ownedPlayersCount}
              maxProgress={totalPlayersCount}
              style={
                {
                  '--circular-progress-stroke':
                    COMPETITION_COLORS[competition?.slug || ''],
                } as CSSProperties
              }
            />
            <LabelL bold>
              <FormattedNumber
                value={ownedPlayersCount / totalPlayersCount}
                maximumFractionDigits={0}
                style="percent"
              />
            </LabelL>
          </Horizontal>
        </Vertical>
      </SBHorizontal>
    </Root>
  );
};

LeaguePageHeader.fragments = {
  competitionCardCollections: gql`
    fragment LeaguePageHeader_competitionCardCollections on CompetitionCardCollections {
      competition {
        slug
        pictureUrl
        displayName
      }
      myStats {
        ownedPlayersCount
        totalPlayersCount
      }
    }
  ` as TypedDocumentNode<LeaguePageHeader_competitionCardCollections>,
};
